'use client';

import { useMemo } from 'react';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { useCurrentLocale } from '@locales/client';
import { format, isThisYear } from 'date-fns';

import { fetcherWithToken } from '@/shared/api/fetcher';
import { useDateLocale } from '@/shared/hooks/useDateLocale';

import { timestampToDate } from '@/shared/libs/timestamp-to-date';

export const useNotifyList = () => {
  const {
    data: notifyList,
    isLoading: isNotifyListLoading,
    mutate: notifyListMutate,
  } = useSWR('/notifications/list', fetcherWithToken);
  const { trigger } = useSWRMutation(
    '/notifications/markRead',
    fetcherWithToken
  );

  return {
    notifyList,
    markReadAllTrigger: trigger,
    notifyListMutate,
    isNotifyListLoading: isNotifyListLoading,
  };
};

export const useNotificationDate = (notifyList) => {
  const locale = useCurrentLocale();
  const dateLocale = useDateLocale(locale);

  const formatGroupHeader = (date: Date) => {
    // if (isToday(date)) return t('today');
    // if (isYesterday(date)) return t('yesterday');

    if (isThisYear(date)) {
      return format(date, 'd MMMM', { locale: dateLocale });
    }

    return format(date, 'd MMMM yyyy', { locale: dateLocale });
  };

  const formatNotificationTime = (date: Date) => {
    return format(date, 'HH:mm', { locale: dateLocale });
  };

  // Группировка уведомлений по дате
  const groupedNotifications = useMemo(() => {
    if (!Array.isArray(notifyList)) return [];

    const groups: Record<string, { date: Date; items: any[] }> = {};

    notifyList.forEach((item) => {
      const date = timestampToDate(item.ts);
      const normalizedDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
      );

      const groupKey = normalizedDate.toISOString();

      if (!groups[groupKey]) {
        groups[groupKey] = {
          date: normalizedDate,
          items: [],
        };
      }

      groups[groupKey].items.push(item);
    });

    // Сортируем группы по дате (новые сверху)
    return Object.values(groups).sort(
      (a, b) => b.date.getTime() - a.date.getTime()
    );
  }, [notifyList]);

  return {
    groupedNotifications,
    formatGroupHeader,
    formatNotificationTime,
  };
};
